<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { isPast, isToday } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useTasksStore } from 'dashboard/stores/tasks';

import Popover from 'dashboard/components-next/popover/Popover.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TaskFormDialog from 'dashboard/components-next/Tasks/TaskFormDialog.vue';
import {
  getTaskDueDate,
  formatTaskEndTime,
} from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  conversationId: { type: Number, required: true },
  contactId: { type: Number, default: null },
  assigneeId: { type: Number, default: null },
});

const { t } = useI18n();
const store = useStore();
const tasksStore = useTasksStore();
const currentUser = useMapGetter('getCurrentUser');

const tasks = ref([]);
const formDialogRef = ref(null);

const pendingCount = computed(
  () => tasks.value.filter(task => task.status === 'pending').length
);

const sortedTasks = computed(() =>
  [...tasks.value].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'completed' ? 1 : -1;
    return a.dueDate - b.dueDate;
  })
);

const hasTasks = computed(() => tasks.value.length > 0);

const loadTasks = async () => {
  tasks.value = await tasksStore.getByConversation(props.conversationId);
};

const urgencyClass = task => {
  if (task.status === 'completed') return 'text-n-slate-10';
  const dueDate = getTaskDueDate(task);
  if (isPast(dueDate) && !isToday(dueDate)) return 'text-n-ruby-9';
  if (isToday(dueDate)) return 'text-n-teal-9';
  return 'text-n-slate-11';
};

const formattedDueDate = task => {
  const dueDate = getTaskDueDate(task);
  const options = task.allDay
    ? { day: '2-digit', month: 'short' }
    : { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' };
  return `${new Intl.DateTimeFormat('pt-BR', options).format(dueDate)}${formatTaskEndTime(task, 'pt-BR')}`;
};

const toggleTaskStatus = async task => {
  const previousStatus = task.status;
  const newStatus = previousStatus === 'completed' ? 'pending' : 'completed';
  task.status = newStatus;
  try {
    await tasksStore.update({ id: task.id, status: newStatus });
  } catch (error) {
    task.status = previousStatus;
    useAlert(error.message ?? t('TASKS.LIST.UPDATE.ERROR_MESSAGE'));
  }
};

const openCreateDialog = () => {
  formDialogRef.value?.openDialog();
};

// `throwErrorMessage` stringifies the raw axios error when the server sends no
// message (e.g. 500 with an HTML body), giving "AxiosError: Request failed...".
const TECHNICAL_ERROR = /^\w*Error: /;

const handleCreate = async taskData => {
  let created;
  try {
    created = await tasksStore.create(taskData);
  } catch (error) {
    const hasServerMessage = !TECHNICAL_ERROR.test(error.message);
    useAlert(
      hasServerMessage ? error.message : t('TASKS.CREATE.MESSAGES.ERROR')
    );
    return;
  }
  formDialogRef.value?.onSuccess();
  tasks.value.push(created);
  useAlert(t('TASKS.CREATE.MESSAGES.SUCCESS'));
  store.dispatch('taskStats/get', currentUser.value.id);
};

watch(() => props.conversationId, loadTasks, { immediate: true });

defineExpose({ pendingCount });
</script>

<template>
  <Popover>
    <template #default="{ isOpen }">
      <slot :is-open="isOpen" />
    </template>
    <template #content>
      <div class="w-80 flex flex-col">
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-strong"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('TASKS.POPOVER.TITLE') }}
          </span>
          <Button
            :label="t('TASKS.POPOVER.ADD_BUTTON')"
            icon="i-lucide-plus"
            size="xs"
            @click="openCreateDialog"
          />
        </div>

        <div class="max-h-80 overflow-y-auto">
          <p
            v-if="!hasTasks"
            class="px-4 py-6 text-sm text-center text-n-slate-11"
          >
            {{ t('TASKS.POPOVER.EMPTY_STATE') }}
          </p>
          <div
            v-for="task in sortedTasks"
            :key="task.id"
            class="flex items-center gap-3 px-4 py-2.5 border-b border-n-weak last:border-b-0"
          >
            <button
              type="button"
              class="flex items-center justify-center size-5 rounded-full border transition-colors shrink-0"
              :class="
                task.status === 'completed'
                  ? 'bg-n-brand border-n-brand text-white'
                  : 'border-n-slate-8 hover:border-n-brand'
              "
              @click="toggleTaskStatus(task)"
            >
              <Icon
                v-if="task.status === 'completed'"
                icon="i-lucide-check"
                class="size-3"
              />
            </button>
            <div class="flex flex-col min-w-0 flex-1">
              <span
                class="text-sm truncate"
                :class="
                  task.status === 'completed'
                    ? 'line-through text-n-slate-10'
                    : 'text-n-slate-12'
                "
              >
                {{ task.title }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ task.assignedTo?.name }}
              </span>
            </div>
            <span class="text-xs shrink-0" :class="urgencyClass(task)">
              {{ formattedDueDate(task) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </Popover>

  <TaskFormDialog
    ref="formDialogRef"
    :conversation-id="conversationId"
    :initial-contact-id="contactId"
    :initial-assigned-to-id="assigneeId"
    @create="handleCreate"
  />
</template>
